// Libs
import classNames from 'classnames/bind';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseSelect, Loading } from '~/components/common';
import { UploadDocument } from '~/components/module/document';
// Others
import { IFileUpload } from '~/utils/interface/common';
import { defaultValuesForm, getFileExtensionFromMimeType, schema } from './helper';
import {
  ACCEPT_DOCUMENT_FILES,
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { BusinessTypeEnum } from '~/utils/enum/common';
import { compareDataUpdate, createFormData } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { editDocument, uploadDocument } from '~/thunks/document/documentThunk';
import { documentActions } from '~/thunks/document/documentSlice';
import {
  IDocument,
  IDocumentUpload,
  IEditDocumentBody,
  IEditDocumentParams,
  IFolderOption,
  IFormDocument,
  IUploadDocumentBody,
  IUploadDocumentParams,
} from '~/utils/interface/document';
import { IFolder, IFolderParams } from '~/utils/interface/folder';
import { getFolderList } from '~/thunks/folder/folderThunk';
// Styles, images, icons
import styles from './FormDocument.module.scss';
import { icons } from '~/assets';

type Props = {
  document?: IDocument;
  businessType: BusinessTypeEnum;
  onClose: () => void;
  onSave?: (data: IFormDocument) => void;
  folders?: IFolder[];
};

const cx = classNames.bind(styles);

const FormDocument = (props: Props) => {
  //#region Destructuring Props
  const { document, businessType, onClose, onSave, folders } = props;
  const { id: targetId } = useParams();
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormDocument>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  const { fields, append, update, remove } = useFieldArray({ name: 'files', control });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingFolder, setIsLoadingFolder] = useState<boolean>(false);
  const [businessIdFolder, setBusinessIdFolder] = useState<string>(EMPTY_STRING);
  // const [paginationFolder, setPaginationFolder] = useState<IPaginationResponse>();
  const [folderOptions, setFolderOptions] = useState<IFolderOption[]>([]);
  const [initialData, setInitialData] = useState<IFormDocument>(defaultValuesForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!targetId) return;

    const params: IFolderParams = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      businessId: targetId,
      businessType: businessType,
      isShareFolder: true,
    };

    handleGetFolderList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!document) return;
    const file: File = new File([], document.url, {
      type: document.mimetype,
    });

    const body: IFormDocument = {
      folder: document?.folder?.id || EMPTY_STRING,
      files: [{ id: document.id, file: file, name: document.name, url: document.url }],
    };

    setInitialData(body);
    reset(body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitFormDocument = (data: IFormDocument) => {
    if (onSave) {
      onSave(data);
      return;
    }

    if (document) {
      handleEditDocument(data);
    } else {
      handleUploadDocument(data);
    }
  };

  const handleEditDocument = async (data: IFormDocument) => {
    if (!data || !document) return;

    setIsLoading(true);

    const dataChange = compareDataUpdate(initialData, data);

    const body: IEditDocumentBody = {
      ...(dataChange.files &&
      dataChange?.files[0]?.file &&
      dataChange?.files[0]?.file?.size > 0 &&
      dataChange?.files[0]
        ? { file: [dataChange?.files[0]] }
        : {}),
      ...(dataChange.folder ? { folderId: dataChange.folder } : {}),
      ...(dataChange?.files && dataChange?.files[0]?.name ? { name: dataChange?.files[0]?.name } : {}),
    };

    const formData = await createFormData(body, 'file');

    const payload: IEditDocumentParams = {
      fileId: document.id,
      formData: formData,
    };

    dispatch(editDocument(payload))
      .unwrap()
      .then(() => {
        dispatch(documentActions.setIsRefreshDocumentList(true));
        handleCloseFormDocument();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUploadDocument = async (data: IFormDocument) => {
    if (!data) return;

    setIsLoading(true);

    const files = data.files.map((file) => ({
      ...file,
      name: file.name,
      file: new File([file.file], file.file.name, {
        type: file.file?.type,
      }),
    }));

    const body: IUploadDocumentBody = {
      files: files,
      businessId: businessIdFolder,
      businessType: businessType,
    };

    const formData = await createFormData(body, 'files');

    const payload: IUploadDocumentParams = {
      folderId: data.folder,
      formData: formData,
    };

    dispatch(uploadDocument(payload))
      .unwrap()
      .then(() => {
        dispatch(documentActions.setIsRefreshDocumentList(true));
        handleCloseFormDocument();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseFormDocument = () => {
    onClose && onClose();
    reset();
  };

  const handleDocumentsChange = (files: IFileUpload[]) => {
    if (document && files[0]?.file) {
      const updateFile: IDocumentUpload[] = [
        {
          name: files[0]?.file?.name?.split('.')[DEFAULT_NUMBER_ZERO] || EMPTY_STRING,
          file: files[0]?.file,
          url: files[0]?.file ? URL.createObjectURL(files[0]?.file) : EMPTY_STRING,
        },
      ];
      update(DEFAULT_NUMBER_ZERO, updateFile[0]);
      return;
    }

    files.forEach((file) => {
      if (!file.file) return;

      append({
        name: file.file?.name?.split('.')[DEFAULT_NUMBER_ZERO] || EMPTY_STRING,
        file: file.file,
        url: file.file ? URL.createObjectURL(file.file) : EMPTY_STRING,
      });
    });
  };

  const handleGetFolderList = (params: IFolderParams) => {
    setIsLoadingFolder(true);

    dispatch(getFolderList(params))
      .unwrap()
      .then((resp) => {
        const { responses } = resp.data;

        const options: IFolderOption[] = responses?.map((folder) => ({
          label: folder?.name,
          value: folder?.id,
          businessId: folder?.businessId,
          businessType: folder?.businessType,
        }));

        setFolderOptions(options);

        // setFolderOptions((prev) => {
        //   const newFolders = responses?.filter(
        //     (newFolder) => !prev?.some((oldFolder) => oldFolder.value === newFolder.id)
        //   );
        //   const convertFolder = newFolders?.map((folder) => ({ label: folder?.name, value: folder?.id }));

        //   return [...prev, ...convertFolder];
        // });
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingFolder(false);
      });
  };

  // const handleLoadMoreFolder = () => {
  // if (!targetId || !paginationFolder) return false;

  //   const params: IFolderParams = {
  //     page: paginationFolder?.page + 1,
  //     limit: DEFAULT_LIMIT_PAGE,
  //     businessId: targetId,
  //     businessType: businessType,
  //   };

  //   handleGetFolderList(params);

  //   if (folderOptions?.length >= paginationFolder?.totalItems) {
  //     return false;
  //   }

  //   return true;
  // };
  //#endregion Handle Function

  return (
    <Dialog
      id='formDocumentComponent'
      open={true}
      onClose={handleCloseFormDocument}
      fullWidth
      maxWidth={'sm'}
    >
      <Box component={'form'}>
        <DialogTitle>{t(document ? 'form_document_title_edit' : 'form_document_title')}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 size={6}>
              <Controller
                name='folder'
                control={control}
                render={({ field: { value, onChange } }) =>
                  targetId ? (
                    <BaseSelect
                      required
                      label={t('form_document_label_folder')}
                      value={value}
                      onChange={(optionSelected: IFolderOption) => {
                        onChange(optionSelected?.value);
                        setBusinessIdFolder(optionSelected?.businessId);
                      }}
                      options={folderOptions}
                      isLoading={isLoadingFolder}
                      disablePortal
                      // onLoadMore={handleLoadMoreFolder}
                      // totalOptions={paginationFolder?.totalItems}
                      errorMessage={errors.folder?.message}
                    />
                  ) : (
                    <BaseSelect
                      required
                      label={t('form_document_label_folder')}
                      value={value}
                      onChange={(optionSelected) => {
                        onChange(optionSelected?.value);
                      }}
                      options={folders?.map((item) => ({ label: item?.name, value: item?.id }))}
                      isLoading={isLoadingFolder}
                      disablePortal
                      // onLoadMore={handleLoadMoreFolder}
                      // totalOptions={paginationFolder?.totalItems}
                      errorMessage={errors.folder?.message}
                    />
                  )
                }
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                name='files'
                control={control}
                render={() => (
                  <UploadDocument
                    accept={ACCEPT_DOCUMENT_FILES}
                    isMultiple={!document}
                    onChange={handleDocumentsChange}
                    errorMessage={errors?.files?.message}
                  />
                )}
              />
              <Box className={cx('fileList')}>
                {fields.map((file, index) => (
                  <Box key={index} className={cx('fileItem')}>
                    {!document && (
                      <img
                        src={icons.commonIconCloseField}
                        alt={t('common_alt_img', { ns: 'common/base' })}
                        className={cx('iconDelete')}
                        onClick={() => remove(index)}
                      />
                    )}

                    <Box className={cx('fileContent')}>
                      <input type='file' hidden name={`files.${index}.file`} />

                      <Box onClick={() => window.open(file?.url)} className={cx('filePreview')}>
                        {file.file?.type.includes('image') ? (
                          <img
                            src={file.url}
                            alt={t('common_alt_img', { ns: 'common/base' })}
                            className={cx('preview')}
                          />
                        ) : (
                          <img
                            src={icons.documentIconDocument}
                            alt={t('common_alt_img', { ns: 'common/base' })}
                            className={cx('preview')}
                          />
                        )}
                      </Box>

                      <Box className={cx('nameFile')}>
                        <InputLabel className={cx('label')}>{t('form_document_label_name')}</InputLabel>

                        <Controller
                          key={file.id || index}
                          name={`files.${index}.name`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              className={cx('inputName')}
                              slotProps={{
                                input: {
                                  className: cx('input'),
                                  endAdornment: (
                                    <InputAdornment position='end'>
                                      <Typography className={cx('typeFile')}>
                                        {file.file?.type && getFileExtensionFromMimeType(file.file?.type)}
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                },
                                formHelperText: { className: cx('errorMessage') },
                              }}
                              name={`files.${index}.name`}
                              data-key={index}
                              value={field.value}
                              onChange={field.onChange}
                              helperText={errors.files && errors.files[index]?.name?.message}
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid2>
          </Grid2>
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' type='button' onClick={handleCloseFormDocument}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='button' onClick={handleSubmit(handleSubmitFormDocument)}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>
      </Box>
      {isLoading && <Loading />}
    </Dialog>
  );
};

export default FormDocument;
